import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { unitsOfMeasureApi } from '../inventoryApi';
import type { UnitOfMeasureListItem } from '../inventoryTypes';
import { sampleUnitOfMeasure } from '../utils/sampleInventory';

/**
 * Create or edit one unit of measure.
 *
 * The help text on the code says what the field costs to get wrong, because this is the one
 * vocabulary in the module whose drift produces wrong NUMBERS rather than untidy ones:
 * quantities are summed by this value.
 */
export function UnitOfMeasureModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: UnitOfMeasureListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(editing?.code ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = code.trim().length > 0 && name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (editing) await unitsOfMeasureApi.update(editing.id, request);
      else await unitsOfMeasureApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the unit.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'unit-of-measure' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit unit' : 'New unit'}
      size="lg"
      id="unit-of-measure-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleUnitOfMeasure();
          setCode(sample.code);
          setName(sample.name);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'unit-of-measure-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="unit-of-measure-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="unit-of-measure-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="unit-of-measure-code"
            label="Code"
            required
            description="The short form written on a row, e.g. pc. This is what a quantity is summed by, so a second spelling of one unit is a stock figure nothing can add up."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={15}
            disabled={saving}
            error={error?.fieldError('code')}
          />

          <TextField
            id="unit-of-measure-name"
            label="Name"
            required
            description="The word a reader picks from, e.g. Piece. Held once among the live rows, like the code."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            disabled={saving}
            error={error?.fieldError('name')}
          />
        </div>

        <TextField
          id="unit-of-measure-description"
          label="Description"
          description="Optional note for an administrator maintaining the vocabulary."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={300}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        {editing ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Every item counted in this unit resolves its code on every read, so correcting a typo
            corrects them all. That also means renaming it to a different unit would restate every
            quantity recorded against it — nothing here can tell those two apart.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
